/**
 * "Book a call" for wire and card deposits: one line (date · time · contact method) that opens a
 * month calendar with the free days, the times for the chosen day (12h or 24h) and the contact
 * method. Everything is shown in the client's own time zone; the call hours themselves are set in
 * CALL_HOURS (shared). The default is the soonest time, 15 minutes from now.
 */
import { useMemo, useState } from 'react';
import { callSlots, CONTACT_METHODS, contactLabel, lastCallTime, soonestCall, type ContactMethod } from '@platform/shared';
import { cx } from '../../../components/ui';

export interface CallChoice {
  callAt: number;
  method: ContactMethod;
}

const WD = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/** The browser's IANA time zone (the client's local time). */
export const clientTimeZone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
};
const city = (tz: string) => (tz.split('/').pop() ?? tz).replace(/_/g, ' ');

function useFormatters(tz: string) {
  return useMemo(() => {
    const key = new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' });
    const t12 = new Intl.DateTimeFormat('en-US', { timeZone: tz, hour: 'numeric', minute: '2-digit', hourCycle: 'h12' });
    const t24 = new Intl.DateTimeFormat('en-GB', { timeZone: tz, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
    return {
      /** "2026-10-08" in the client's time zone. */
      dayKey: (t: number) => key.format(t),
      time: (t: number, h24: boolean) => (h24 ? t24.format(t) : t12.format(t).replace(' AM', ' am').replace(' PM', ' pm')),
    };
  }, [tz]);
}

const keyOf = (y: number, m: number, d: number) => `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
const parseKey = (k: string) => k.split('-').map(Number) as [number, number, number];
const dayLabel = (k: string, today: string) => {
  const [y, m, d] = parseKey(k);
  return `${k === today ? 'Today' : WD[new Date(Date.UTC(y, m - 1, d)).getUTCDay()]}, ${d} ${MONTHS[m - 1]!.slice(0, 3)}`;
};

export function CallPicker({ value, onChange, tz, disabled }: { value: CallChoice; onChange: (v: CallChoice) => void; tz: string; disabled?: boolean }) {
  const [open, setOpen] = useState(false);
  const [h24, setH24] = useState(false);
  const f = useFormatters(tz);
  const now = Date.now();
  const today = f.dayKey(now);

  // Every bookable time (the soonest one plus the 30-minute slots) grouped by the client's local day.
  const byDay = useMemo(() => {
    const soonest = soonestCall(now);
    const all = [soonest, ...callSlots(soonest + 1, lastCallTime(now))];
    const map = new Map<string, number[]>();
    for (const t of all) {
      const k = f.dayKey(t);
      map.set(k, [...(map.get(k) ?? []), t]);
    }
    return map;
    // Recomputed when the default moves (the parent refreshes it every 30 s) or the time zone changes.
  }, [f, Math.floor(now / 60_000)]); // eslint-disable-line react-hooks/exhaustive-deps

  const selDay = f.dayKey(value.callAt);
  const [view, setView] = useState<[number, number]>(() => {
    const [y, m] = parseKey(selDay);
    return [y, m - 1];
  });
  const [vy, vm] = view;
  const firstKey = [...byDay.keys()][0] ?? today;
  const lastKey = [...byDay.keys()].at(-1) ?? today;
  const curMonth = keyOf(vy, vm, 1).slice(0, 7);
  const canPrev = curMonth > firstKey.slice(0, 7);
  const canNext = curMonth < lastKey.slice(0, 7);
  const pad = new Date(Date.UTC(vy, vm, 1)).getUTCDay();
  const dim = new Date(Date.UTC(vy, vm + 1, 0)).getUTCDate();
  const times = byDay.get(selDay) ?? [];
  const [sy, sm, sd] = parseKey(selDay);

  const pickDay = (k: string) => {
    const ts = byDay.get(k);
    if (!ts?.length) return;
    // Keep the same clock time on the new day when it exists, else the first time of that day.
    const clock = f.time(value.callAt, true);
    onChange({ ...value, callAt: ts.find((t) => f.time(t, true) === clock) ?? ts[0]! });
  };
  const move = (d: number) => setView(([y, m]) => (m + d < 0 ? [y - 1, 11] : m + d > 11 ? [y + 1, 0] : [y, m + d]));

  return (
    <div className="flex flex-col gap-3">
      <button
        type="button"
        disabled={disabled}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className={cx(
          'flex w-full items-center gap-3 rounded-[14px] border bg-track px-4 py-3.5 text-left text-[15px] disabled:opacity-50 md:text-[16px]',
          open ? 'border-line-strong' : 'border-transparent hover:border-line-strong',
        )}
      >
        <span className="min-w-0 flex-1">
          <b className="font-semibold">{dayLabel(selDay, today)}</b> <span className="text-muted">·</span> <b className="font-semibold">{f.time(value.callAt, h24)}</b>{' '}
          <span className="text-muted">·</span> <b className="font-semibold">{contactLabel(value.method)}</b> <span className="text-[13px] text-muted">· your time, {city(tz)}</span>
        </span>
        <span aria-hidden className="text-[13px] text-muted">
          {open ? '▴' : '▾'}
        </span>
      </button>

      {open && (
        <div className="flex flex-col gap-3">
          <div className="grid overflow-hidden rounded-[18px] border border-line bg-card sm:grid-cols-[minmax(0,1fr)_210px]">
            <div className="min-w-0 border-b border-line p-4 sm:border-r sm:border-b-0 md:p-5">
              <div className="mb-3.5 flex items-center justify-between">
                <b className="text-[17px] font-semibold">
                  {MONTHS[vm]} <span className="font-normal text-muted">{vy}</span>
                </b>
                <div className="flex gap-1">
                  <button type="button" aria-label="Previous month" disabled={!canPrev} onClick={() => move(-1)} className="grid size-8 place-items-center rounded-[8px] text-[18px] hover:bg-track disabled:text-line-strong disabled:hover:bg-transparent">
                    ‹
                  </button>
                  <button type="button" aria-label="Next month" disabled={!canNext} onClick={() => move(1)} className="grid size-8 place-items-center rounded-[8px] text-[18px] hover:bg-track disabled:text-line-strong disabled:hover:bg-transparent">
                    ›
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-7 gap-1.5">
                {WD.map((w) => (
                  <span key={w} className="pb-1.5 text-center text-[11px] font-medium tracking-[.06em] text-muted">
                    {w.toUpperCase()}
                  </span>
                ))}
                {Array.from({ length: pad }, (_, i) => (
                  <span key={`p${i}`} />
                ))}
                {Array.from({ length: dim }, (_, i) => {
                  const k = keyOf(vy, vm, i + 1);
                  const on = byDay.has(k);
                  const sel = k === selDay;
                  return (
                    <button
                      key={k}
                      type="button"
                      disabled={!on}
                      aria-pressed={sel}
                      aria-label={dayLabel(k, today)}
                      onClick={() => pickDay(k)}
                      className={cx(
                        'relative grid aspect-square place-items-center rounded-[8px] text-[14px]',
                        sel ? 'bg-fg font-semibold text-bg' : on ? 'bg-track font-semibold text-fg hover:ring-1 hover:ring-line-strong' : 'text-muted/50',
                        k === today && "after:absolute after:bottom-[5px] after:size-1 after:rounded-full after:bg-current after:content-['']",
                      )}
                    >
                      {i + 1}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="flex min-w-0 flex-col">
              <div className="flex items-center justify-between gap-2 px-3.5 pt-4 pb-3 md:pt-5">
                <b className="text-[15px] font-semibold whitespace-nowrap">
                  {WD[new Date(Date.UTC(sy, sm - 1, sd)).getUTCDay()]}
                  <span className="font-normal text-muted">
                    , {MONTHS[sm - 1]!.slice(0, 3)} {sd}
                  </span>
                </b>
                <span className="inline-flex rounded-[8px] border border-line p-0.5">
                  {[false, true].map((x) => (
                    <button key={String(x)} type="button" aria-pressed={h24 === x} onClick={() => setH24(x)} className={cx('rounded-[6px] px-2 py-1 text-[12px] font-medium', h24 === x ? 'bg-track text-fg' : 'text-muted')}>
                      {x ? '24h' : '12h'}
                    </button>
                  ))}
                </span>
              </div>
              <div className="flex max-h-[318px] flex-wrap gap-2 overflow-y-auto px-3.5 pb-3.5 sm:flex-col sm:flex-nowrap">
                {times.map((t) => {
                  const sel = t === value.callAt;
                  return (
                    <button
                      key={t}
                      type="button"
                      aria-pressed={sel}
                      onClick={() => onChange({ ...value, callAt: t })}
                      className={cx(
                        'num flex flex-[1_0_96px] items-center justify-center gap-2.5 rounded-[8px] border px-2.5 py-2 text-[14px] font-medium sm:flex-none',
                        sel ? 'border-fg bg-fg text-bg' : 'border-line bg-card hover:border-line-strong',
                      )}
                    >
                      <i className={cx('size-1.5 rounded-full', sel ? 'bg-current' : 'bg-muted')} />
                      {f.time(t, h24)}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="How should we contact you?">
              {CONTACT_METHODS.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  role="radio"
                  aria-checked={value.method === c.id}
                  onClick={() => onChange({ ...value, method: c.id })}
                  className={cx('rounded-full border px-3.5 py-2 text-[14px] font-medium', value.method === c.id ? 'border-accent bg-accent-bg text-accent-fg' : 'border-line-strong')}
                >
                  {c.label}
                </button>
              ))}
            </div>
            <button type="button" onClick={() => setOpen(false)} className="rounded-full bg-pill px-5 py-2.5 text-[14px] font-semibold">
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
